import { useCallback, useEffect, useRef, useState } from "react";
import {
  apiDelete,
  apiGet,
  apiPost,
  apiPostForm,
  apiPut,
  apiUrl,
  prettifyApiError,
} from "@/shared/api";

export type ThreadListItem = {
  id: number;
  account_id: number;
  source: string;
  source_slug: string;
  source_label: string;
  external_thread_id: string;
  external_order_id: string;
  subject: string;
  customer_name: string;
  customer_org_name: string;
  last_message_at: string | null;
  last_message_preview: string;
  message_count: number;
  has_unread: boolean;
  needs_reply: boolean;
  internal_status: string;
};

export type ThreadDetail = ThreadListItem & {
  topic_type: string;
  customer_id: string;
  note: string;
  /** Perché l'antispam l'ha fermata (vista «Spam»); vuota per gli altri. */
  spam_motivi?: string[];
  messages: ThreadMessage[];
};

export type ThreadMessage = {
  id: number;
  external_message_id: string;
  sender_name: string;
  sender_type: string;
  sender_org_name: string;
  sender_org_type: string;
  body_html: string;
  body_plain: string;
  /** Cache traduzione italiana persistente (migration 0025).
   *  - ``null``: traduzione mai eseguita → toggle "IT" chiama API runtime
   *  - ``""``: tradotto ma sorgente vuota / non traducibile
   *  - stringa: traduzione cached → toggle istantaneo, niente API call */
  body_translated_it: string | null;
  /** ISO 639-1 della lingua rilevata al momento della traduzione (debug). */
  source_lang_detected: string | null;
  translated_at: string | null;
  created_at_remote: string | null;
  recipients: Array<Record<string, unknown>>;
  attachments: ThreadAttachment[];
};

export type ThreadAttachment = {
  id: number;
  file_name: string;
  content_type: string;
  file_size: number | null;
};

export type UploadedAttachment = {
  id: number;
  file_name: string;
  content_type: string;
  file_size: number;
};

export type ReplyResponse = {
  thread: ThreadDetail;
  message: ThreadMessage;
};

export type ThreadList = {
  items: ThreadListItem[];
  total: number;
  unread: number;
  needs_reply: number;
};

export type MailAccount = {
  id: number;
  source: string;
  slug: string;
  label: string;
  enabled: boolean;
};

type Filters = {
  q: string;
  /** Vista "Stato": "" (tutti) | "da_gestire" | "in_corso" | "chiusi" | "spam". */
  view: string;
  accountId: number | null;
  /** ISO date inclusivo: filtra thread con last_message_at >= dateFrom. */
  dateFrom?: string | null;
  /** ISO date inclusivo: filtra thread con last_message_at <= dateTo. */
  dateTo?: string | null;
};

export function useThreads(filters: Filters): {
  data: ThreadList;
  isLoading: boolean;
  error: string | null;
  info: string | null;
  refresh: () => Promise<void>;
  syncNow: () => Promise<void>;
  isSyncing: boolean;
} {
  const [data, setData] = useState<ThreadList>({
    items: [],
    total: 0,
    unread: 0,
    needs_reply: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Audit 2026-05-29: slot ``info`` separato dallo slot ``error`` cosi' il
  // messaggio di esito positivo del sync non viene reso in rosso (stesso
  // pattern di ordini/api.ts). ``info`` → box verde in ThreadList.
  const [info, setInfo] = useState<string | null>(null);
  const inFlight = useRef<AbortController | null>(null);
  // Audit 2026-05-29: ref per cancellare il setTimeout di refresh post-sync
  // se il componente smonta nei 2.5s (prima era orfano → setState su hook
  // smontato). Stesso pattern di ordini/api.ts.
  const refreshTimerRef = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    if (inFlight.current) inFlight.current.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (filters.q.trim()) params.set("q", filters.q.trim());
      if (filters.view) params.set("view", filters.view);
      if (filters.accountId) params.set("account_id", String(filters.accountId));
      if (filters.dateFrom) params.set("date_from", filters.dateFrom);
      if (filters.dateTo) params.set("date_to", filters.dateTo);
      const response = await apiGet<ThreadList>(`/api/mail/threads?${params.toString()}`, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      setData(response);
      setError(null);
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(prettifyApiError(err));
    } finally {
      // Spegni lo spinner SOLO se questa e' ancora la richiesta corrente: una
      // richiesta soppressa (abortita da una piu' recente, es. digitazione
      // rapida nella ricerca) non deve azzerare isLoading mentre la nuova e'
      // ancora in volo -> evita il flicker "Nessuna conversazione".
      if (inFlight.current === controller) {
        inFlight.current = null;
        setIsLoading(false);
      }
    }
  }, [filters.q, filters.view, filters.accountId, filters.dateFrom, filters.dateTo]);

  useEffect(() => {
    void refresh();
    return () => {
      inFlight.current?.abort();
      // Audit 2026-05-29: cancella il timer di refresh post-sync pendente.
      if (refreshTimerRef.current !== null) {
        window.clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [refresh]);

  const syncNow = useCallback(async () => {
    setIsSyncing(true);
    try {
      await apiPost("/api/mail/sync");
      // Audit 2026-05-29: esito positivo nello slot ``info`` (box verde),
      // non in ``error`` (box rosso fuorviante).
      setError(null);
      setInfo("Sync messaggi avviato. Aggiorno la lista tra pochi secondi.");
      // Audit 2026-05-29: timer tracciato in ``refreshTimerRef`` (cleanup su
      // unmount). Cancello l'eventuale precedente prima di ri-schedularlo.
      if (refreshTimerRef.current !== null) {
        window.clearTimeout(refreshTimerRef.current);
      }
      refreshTimerRef.current = window.setTimeout(() => {
        refreshTimerRef.current = null;
        setInfo(null);
        void refresh();
      }, 2500);
    } catch (err) {
      setInfo(null);
      setError(prettifyApiError(err));
    } finally {
      setIsSyncing(false);
    }
  }, [refresh]);

  return { data, isLoading, error, info, refresh, syncNow, isSyncing };
}

/** Thread collegati a un ordine, filtrati per match ESATTO su
 *  ``external_order_id``. Il backend cerca ``q`` in substring su
 *  external_order_id/oggetto/cliente, quindi puo' restituire thread di ALTRI
 *  ordini (ref che e' sottostringa, oppure il ref citato in un oggetto):
 *  filtriamo qui per non puntare al thread sbagliato (review round3). */
export async function fetchThreadsByOrder(
  reference: string,
): Promise<{ items: ThreadListItem[]; total: number }> {
  const qs = new URLSearchParams({ q: reference, limit: "10" });
  const res = await apiGet<ThreadList>(`/api/mail/threads?${qs.toString()}`);
  const exact = res.items.filter((t) => t.external_order_id === reference);
  return { items: exact, total: exact.length };
}

export async function fetchThreadDetail(threadId: number): Promise<ThreadDetail> {
  return apiGet<ThreadDetail>(`/api/mail/threads/${threadId}`);
}

export async function updateThreadStatus(
  threadId: number,
  internalStatus: string,
): Promise<ThreadDetail> {
  return apiPut<ThreadDetail>(`/api/mail/threads/${threadId}/status`, {
    internal_status: internalStatus,
  });
}

export async function fetchAccounts(): Promise<MailAccount[]> {
  return apiGet<MailAccount[]>("/api/mail/accounts");
}

export async function sendThreadReply(
  threadId: number,
  body: string,
  attachmentIds: number[] = [],
  /** Destinatario Mirakl (item 16). Ignorato dagli altri canali. */
  recipient: "customer" | "operator" | "all" = "customer",
): Promise<ReplyResponse> {
  return apiPost<ReplyResponse>(`/api/mail/threads/${threadId}/reply`, {
    body,
    attachment_ids: attachmentIds,
    recipient,
  });
}

export async function uploadThreadAttachments(
  threadId: number,
  files: File[],
): Promise<UploadedAttachment[]> {
  const form = new FormData();
  files.forEach((file) => form.append("files", file));
  return apiPostForm<UploadedAttachment[]>(`/api/mail/threads/${threadId}/attachments`, form);
}

export function attachmentDownloadUrl(attachmentId: number): string {
  return apiUrl(`/api/mail/attachments/${attachmentId}/download`);
}

/**
 * Scarica i bytes di un allegato dal backend, sollevando un ``Error`` con
 * messaggio leggibile se la risposta non e' ok. Il chiamante decide come
 * salvare il blob (in Electron l'anchor con ``download`` viene intercettato
 * da ``will-download`` -> ~/Downloads).
 *
 * Bug 2026-06-15: prima il download era un ``<a href download>`` puro. Se il
 * backend rispondeva 4xx/5xx (es. 404 dopo che un sync ha rigenerato gli id
 * degli allegati, o URL firmato del marketplace scaduto) l'errore era
 * invisibile e l'utente vedeva "non succede niente". Ora viene propagato e
 * mostrato in UI.
 */
export async function fetchAttachmentBlob(attachmentId: number): Promise<Blob> {
  const res = await fetch(attachmentDownloadUrl(attachmentId));
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { error?: { message?: string }; detail?: string };
      detail = body.error?.message ?? body.detail ?? detail;
    } catch {
      // Nessun corpo JSON: tieni il fallback HTTP status.
    }
    throw new Error(detail);
  }
  return res.blob();
}

// ── Traduzione messaggi (#6 legacy parity) ────────────────────────────

export type TranslateMessageResponse = {
  message_id: number;
  target_lang: string;
  translated_text: string;
  /** True quando la traduzione e' stata servita dalla cache DB
   *  (``mail.messages.body_translated_it``), False se backend ha appena
   *  chiamato il provider AI. Frontend puo' nascondere lo spinner di
   *  traduzione se ``cached`` arriva ``true`` (response istantanea). */
  cached?: boolean;
};

export type TranslateThreadResponse = {
  target_lang: string;
  items: TranslateMessageResponse[];
  errors: Record<string, string>;
};

export async function translateMessage(
  messageId: number,
  targetLang: string = "italiano",
): Promise<TranslateMessageResponse> {
  return apiPost<TranslateMessageResponse>(`/api/mail/messages/${messageId}/translate`, {
    target_lang: targetLang,
  });
}

export async function translateThread(
  threadId: number,
  targetLang: string = "italiano",
): Promise<TranslateThreadResponse> {
  return apiPost<TranslateThreadResponse>(`/api/mail/threads/${threadId}/translate`, {
    target_lang: targetLang,
  });
}

/** Risposta di ``translateDraft``: contiene il testo tradotto e la
 *  ``target_lang`` effettivamente usata (utile quando ``auto_detected``
 *  per mostrarlo al' operatore con un tag tipo "tradotto in: francese"). */
export type TranslateDraftResponse = {
  translated_text: string;
  target_lang: string;
  auto_detected: boolean;
};

/** Traduce la BOZZA dell'operatore nella lingua del cliente.
 *
 *  Se ``targetLang`` non e' specificata, il backend la auto-rileva dai
 *  messaggi inbound del thread. Audit 2026-05-20 (Davide): "Devo poter
 *  inviare il messaggio nella lingua del cliente". */
export async function translateDraft(
  threadId: number,
  text: string,
  targetLang?: string | null,
): Promise<TranslateDraftResponse> {
  return apiPost<TranslateDraftResponse>(`/api/mail/threads/${threadId}/translate-draft`, {
    text,
    target_lang: targetLang ?? null,
  });
}

// ── Mittenti esclusi per account (#6 legacy parity) ───────────────────

export type ExcludedSender = {
  id: number;
  account_id: number;
  pattern: string;
  created_at: string;
};

export async function listExcludedSenders(accountId: number): Promise<ExcludedSender[]> {
  const res = await apiGet<{ items: ExcludedSender[] }>(
    `/api/mail/accounts/${accountId}/excluded-senders`,
  );
  return res.items;
}

export async function addExcludedSender(
  accountId: number,
  pattern: string,
): Promise<ExcludedSender> {
  return apiPost<ExcludedSender>(`/api/mail/accounts/${accountId}/excluded-senders`, {
    pattern,
  });
}

export async function removeExcludedSender(excludedId: number): Promise<void> {
  return apiDelete(`/api/mail/excluded-senders/${excludedId}`);
}

// ── Tracking SDA da Bracchi ─────────────────────────────────────────────

/** Step canonico del tracking SDA letto da Bracchi. Stringa vuota =
 *  sconosciuto/nessun dato. Determina il colore del badge nel popup
 *  "Storico spedizione". */
export type TrackingStep =
  | "Presa in carico"
  | "In transito"
  | "In consegna"
  | "In giacenza"
  | "Consegnata"
  | "In restituzione"
  /** La spedizione torna al mittente (26/10): rosso su madre e colli. */
  | "Ritorno al mittente"
  /** Tornata a noi: la «consegnata» dopo un ritorno non e' al cliente. */
  | "Rientrata al mittente"
  | "";

/** Singolo evento-scan reale del corriere, come restituito da
 *  ``GET /api/tracking``. ``timestamp`` ISO 8601 UTC; ``status`` testo
 *  italiano del corriere; ``location`` puo' essere vuoto. */
export type TrackingEvent = {
  timestamp: string;
  status: string;
  location: string;
  step: TrackingStep;
  /** Codice del collo a cui si riferisce l'evento; "" (o assente) se la
   *  spedizione ha un collo solo. */
  collo?: string;
};

/** Stato di un singolo collo di una spedizione multi-collo: con Bracchi un
 *  collo puo' essere consegnato e l'altro no. */
export type ColloStato = {
  codice: string;
  /** Testo del corriere dell'ultimo evento del collo. */
  stato: string;
  step: TrackingStep;
  /** ISO 8601 dell'ultima scansione; null se il collo non ne ha. */
  ultimo_evento: string | null;
  consegnato: boolean;
  /** Il collo torna (o e' tornato) al mittente. */
  ritorno_mittente?: boolean;
};

/** Risultato di tracking per un singolo codice (LDV master o collo). Quando
 *  il codice non e' ancora tracciato (appena creato) o Bracchi e'
 *  irraggiungibile, ``events`` e' ``[]`` e ``current_step`` e' ``""``: il popup
 *  degrada con "Tracking non ancora disponibile" + link al portale Bracchi. */
export type TrackingResult = {
  code: string;
  /** Es. "CLM SDA". Puo' essere "". */
  product: string;
  current_step: TrackingStep;
  /** Es. "Consegna prevista entro mercoledì 1 luglio 2026". Puo' essere "". */
  eta: string;
  /** LDV master a cui il codice appartiene (puo' essere ""). */
  parent_ldv: string;
  /** Codici dei colli fratelli (puo' essere []). */
  parcel_codes: string[];
  /** Eventi ASC per timestamp (dal piu' vecchio al piu' recente). [] se
   *  non ancora tracciato / Bracchi irraggiungibile. */
  events: TrackingEvent[];
  /** true = Bracchi non ha risposto (errore): il popup mostra "servizio
   *  momentaneamente non disponibile" invece del fuorviante "non ancora preso
   *  in carico". Default false. */
  unavailable?: boolean;
  /** true = questi eventi vengono dal nostro archivio, non da una lettura
   *  appena fatta: il corriere non ha risposto adesso. I dati sono veri, solo
   *  non freschi — vanno mostrati con la loro data, non spacciati per attuali. */
  stale?: boolean;
  /** ISO 8601 dell'ultima lettura RIUSCITA dal corriere. null = non ne abbiamo
   *  mai avuta una per questo codice. */
  updated_at?: string | null;
  /** Stato per collo (spedizioni multi-collo). Assente o [] per le timeline
   *  d'archivio scritte prima di questo campo. */
  colli?: ColloStato[];
  /** Un evento dice che il pacco torna al mittente: il pannello lo dice in rosso. */
  ritorno_mittente?: boolean;
};

export type TrackingResponse = {
  results: TrackingResult[];
  /** false = interruttore OM_BRACCHI_TRACKING spento: nessuna lettura dal
   *  vivo, il popup dice «non ancora attivo» invece di «non disponibile». */
  attivo: boolean;
  /** Chi legge il tracking («Poste» o «Bracchi»): il pannello lo nomina. */
  fonte?: string;
};

/**
 * Recupera il tracking SDA da Bracchi per uno o piu' codici (LDV master +
 * colli). Max 30 codici per chiamata (limite backend); il chiamante passa
 * gia' la lista deduplicata. Lancia in caso di errore HTTP/rete: il popup
 * gestisce la degradazione (mostra il link al portale Bracchi, non crasha).
 *
 * Endpoint: ``GET /api/tracking?codes=CODE1,CODE2`` →
 * ``{ results: TrackingResult[], attivo: boolean }``. Ogni chiamata e' una
 * lettura dal vivo: non c'e' cache da scavalcare, "Aggiorna" richiama e basta.
 *
 * ``signal`` serve a chi smonta il pannello prima della risposta (cambio
 * thread/ordine rapido): senza, la GET resta in volo e la risposta di un
 * tracking vecchio puo' arrivare dopo quella nuova. Stesso contratto delle
 * sorelle in ``features/clienti/api.ts``.
 */
export async function fetchTracking(
  codes: string[],
  signal?: AbortSignal,
): Promise<TrackingResponse> {
  const query = `codes=${encodeURIComponent(codes.join(","))}`;
  return apiGet<TrackingResponse>(`/api/tracking?${query}`, signal ? { signal } : undefined);
}

// ── Bot pause (WhatsApp) ───────────────────────────────────────────────

/** Stato pausa bot su un thread WhatsApp.
 *  - ``human_mode``: true quando l'operatore ha preso in carico la chat
 *    (senza scadenza automatica).
 *  - ``human_until``: ISO 8601 UTC della scadenza temporizzata (null se
 *    non impostata). Se nel futuro, il bot è in pausa temporanea. */
export type BotStatus = {
  human_mode: boolean;
  human_until: string | null;
};

/** Recupera lo stato del bot su un thread WhatsApp.
 *  Lancia in caso di errore di rete/backend (chiamante gestisce
 *  degradazione silenziosa). */
export async function fetchBotStatus(threadId: number): Promise<BotStatus> {
  return apiGet<BotStatus>(`/api/mail/threads/${threadId}/bot-status`);
}

/** Riattiva il bot su un thread WhatsApp (cancella la pausa).
 *  Endpoint: DELETE /api/mail/threads/{id}/bot-pause → { ok: true } */
export async function resumeBot(threadId: number): Promise<{ ok: boolean }> {
  // apiDelete non ritorna body; usiamo fetchWithContext direttamente
  // tramite apiGet<{ok:boolean}> sarebbe GET — il backend usa DELETE.
  // Usiamo apiPost vuoto non è corretto; il backend è DELETE quindi
  // utilizziamo apiDelete e costruiamo il return value.
  // Il backend risponde 200 { ok: true } ma apiDelete ignora il body (204).
  // Usiamo una DELETE raw con body: ricicliamo apiGet pattern ma con DELETE.
  // NOTA: apiDelete è void (ignora body). Per ricevere { ok: true } usiamo
  // fetch diretto tramite lo stesso helper. In alternativa, accettiamo void
  // e restituiamo { ok: true } localmente.
  await apiDelete(`/api/mail/threads/${threadId}/bot-pause`);
  return { ok: true };
}
